'use client';
import { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Button,
  MenuItem,
  FormControl,
  InputLabel,
  Select as MuiSelect,
  OutlinedInput,
  Grid,
  CircularProgress,
  IconButton,
  Stack,
  LinearProgress
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import { useMasterVehicleList } from '../../stores/masterVehicleStore';
import DataTableDailyFuelSummary from './DataTableDailyFuelSummary';
import {
  filterReportFuelSummary,
  fuelConsumptionCount,
  fillVolumeCount,
  residualVolumeCount,
  totalDistanceCount,
  totalDurationOnCount,
  totalDurationOffCount,
  fuelConsumptionCanbusCount
} from './constant';
import { timezone } from '../../utils/timeZone';
import { useLanguage } from '../../utils/i18n';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import dayjs from 'dayjs';
import { useNotifier } from '../../components/Notifier';
import { useCompanyDataList } from '../../stores/companyStore';
import PickerField from '../../components/PickerField';
import usePickerPagination from '../../hooks/usePickerPagination';
import ExcelJS from 'exceljs';
import DownloadIcon from '@mui/icons-material/CloudDownloadOutlined';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import LocalGasStationIcon from '@mui/icons-material/LocalGasStation';
import PropaneIcon from '@mui/icons-material/Propane';
import InvertColorsIcon from '@mui/icons-material/InvertColors';
import RouteIcon from '@mui/icons-material/Route';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import PauseCircleOutlineIcon from '@mui/icons-material/PauseCircleOutline';
const getCompanyIdFromStorage = () => {
  try {
    if (typeof window !== 'undefined' && window.localStorage) {
      const stored = localStorage.getItem('companyId');
      if (stored && String(stored).toLowerCase() !== 'all') {
        return String(stored);
      }
    }
  } catch (err) {
    console.warn('cannot read localStorage', err);
  }
  return '';
};

const ResidualVolumeCard = ({ residualData, t }) => {
  const [page, setPage] = useState(0);
  const itemsPerPage = 6;
  const { list, grandTotal } = residualData;

  const totalPages = Math.ceil(list.length / itemsPerPage);
  const currentList = list.slice(page * itemsPerPage, (page + 1) * itemsPerPage);

  return (
    <Box
      sx={{
        bgcolor: 'rgba(72, 93, 154, 0.04)',
        border: '1px solid',
        borderColor: 'rgba(72, 93, 154, 0.15)',
        borderRadius: 3,
        p: 2,
        display: 'flex',
        flexDirection: 'column',
        transition: 'all 0.2s',
        height: '100%',
        '&:hover': {
          boxShadow: '0 4px 12px rgba(72, 93, 154, 0.1)',
          borderColor: '#485D9A'
        }
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
        <Box sx={{ p: 1.5, bgcolor: '#ffffff', borderRadius: 2, display: 'flex', boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}>
          <InvertColorsIcon sx={{ color: '#01FF05', fontSize: 28 }} />
        </Box>
        <Box sx={{ flex: 1 }}>
          <Typography variant="body2" color="text.secondary" fontWeight="500">
            {t('dailyFuel.residualVol', 'Residual Volume')}
          </Typography>
          <Typography variant="h5" fontWeight="700" color="text.primary" sx={{ mt: 0.5 }}>
            {grandTotal.toFixed(2)}{' '}
            <Typography component="span" variant="body2" color="text.secondary" fontWeight="600">
              Liter
            </Typography>
          </Typography>
        </Box>
      </Box>

      {list.length > 0 && (
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
          <Box sx={{ flex: 1 }}>
            {currentList.map((item, idx) => (
              <Box
                key={idx}
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  py: 0.5,
                  borderBottom: idx < currentList.length - 1 ? '1px dashed rgba(0,0,0,0.1)' : 'none'
                }}
              >
                <Typography variant="body2" fontWeight="500">
                  {item.license_plate}
                </Typography>
                <Typography variant="body2" fontWeight="600" color="primary">
                  {item.residual.toFixed(2)} L
                </Typography>
              </Box>
            ))}
          </Box>
          {totalPages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', mt: 1, gap: 1 }}>
              <IconButton size="small" onClick={() => setPage((p) => Math.max(0, p - 1))} disabled={page === 0}>
                <KeyboardArrowLeftIcon fontSize="small" />
              </IconButton>
              <Typography variant="caption" color="text.secondary">
                {page + 1} / {totalPages}
              </Typography>
              <IconButton size="small" onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))} disabled={page === totalPages - 1}>
                <KeyboardArrowRightIcon fontSize="small" />
              </IconButton>
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
};

function DailyFuelSummaryPageContent() {
  const { t } = useLanguage();
  const notify = useNotifier();
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [loadingText, setLoadingText] = useState('');
  const [summaryData, setSummaryData] = useState({
    fuelConsumption: '0',
    fuelConsumptionCanbus: '0',
    fillVolume: '0',
    residualVolume: '0',
    totalDistance: '0',
    durationOn: '00:00:00',
    durationOff: '00:00:00'
  });
  const [dateError, setDateError] = useState('');

  const [tableSearchQuery, setTableSearchQuery] = useState('');

  const [fuelSource, setFuelSource] = useState('all');

  const [periodType, setPeriodType] = useState('today');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');

  useEffect(() => {
    if (!periodType) return;
    if (periodType === 'custom' && fromDate && toDate) return;

    const now = dayjs();
    if (periodType === 'custom') {
      if (!fromDate) {
        setFromDate('');
        setToDate('');
      }
      return;
    }

    let from;
    let to = now;

    if (periodType === 'today') {
      from = now.startOf('day');
      to = now.second(59);
    } else if (periodType === 'week' || periodType === 'last_week') {
      from = now.subtract(7, 'day').startOf('day');
      to = now.endOf('day');
    } else if (periodType === 'month' || periodType === 'last_month') {
      from = now.subtract(1, 'month').startOf('day');
      to = now.endOf('day');
    } else {
      return;
    }

    if (from && to) {
      setFromDate(from.format('YYYY-MM-DD HH:mm:ss'));
      setToDate(to.format('YYYY-MM-DD HH:mm:ss'));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [periodType]);

  const [selectedCompanyIds, setSelectedCompanyIds] = useState(() => {
    const defaultId = getCompanyIdFromStorage();
    return defaultId ? [defaultId] : [];
  });
  const companyPickerParams = usePickerPagination({
    fetchHook: useCompanyDataList,
    buildArgs: (state) => [state.page, state.pageSize, state.searchKey, state.searchValue],
    searchKey: 'name'
  });
  const companyOptions = companyPickerParams.options;

  const selectedCompaniesParam = useMemo(() => {
    if (selectedCompanyIds.length === 0 || selectedCompanyIds.includes('all')) {
      return companyOptions.map((c) => ({ value: c.value }));
    }
    return selectedCompanyIds.map((id) => ({ value: id }));
  }, [selectedCompanyIds, companyOptions]);

  const validateDateRange = (start, end) => {
    if (!start || !end) return false;
    const diffMinutes = end.diff(start, 'minute');
    if (diffMinutes > 31 * 24 * 60) {
      setDateError(t('dailyFuel.maxTime31Days', 'Maximum time span of 31 days'));
      return false;
    } else if (diffMinutes < 0) {
      setDateError(t('dailyFuel.startTimeCant', 'The start time cannot exceed the end time'));
      return false;
    }
    setDateError('');
    return true;
  };

  const vehiclePickerParams = usePickerPagination({
    fetchHook: useMasterVehicleList,
    buildArgs: (state) => [state.page, state.pageSize, state.companyId, state.searchKey, state.searchValue],
    formatOption: (p) => ({
      value: p.id,
      label: p.license_plate,
      original: p
    }),
    searchKey: 'license_plate',
    companyId: selectedCompaniesParam
  });
  const vehicleList = vehiclePickerParams.options;

  const vehicleOptionsNormalized = useMemo(
    () =>
      (vehicleList || []).map((c) => ({
        value: String(c.id ?? c.vehicle_id ?? c.value ?? c),
        label: c.original?.license_plate ?? c.license_plate ?? c.label ?? c.name ?? String(c.id ?? ''),
        companyName:
          c.original?.join?.company_name ||
          c.original?.company_name ||
          c.join?.company_name ||
          c.company_name ||
          c.companyName ||
          'Unknown Company',
        raw: c.raw ?? c
      })),
    [vehicleList]
  );

  const [selectedVehicles, setSelectedVehicles] = useState([]);
  const selectedVehicleIds = useMemo(() => {
    return selectedVehicles.map((v) => String(v.value));
  }, [selectedVehicles]);

  const handleVehicleChange = (ids) => {
    const newSelected = ids.map((id) => {
      const strId = String(id);
      const match = vehicleOptionsNormalized.find((v) => String(v.value) === strId);
      if (match) return match;
      const prevMatch = selectedVehicles.find((v) => String(v.value) === strId);
      if (prevMatch) return prevMatch;

      const rawMatch =
        (vehicleList || []).find((v) => String(v.value) === strId) ||
        (vehiclePickerParams.listOptions || []).find((v) => String(v.value) === strId);
      if (rawMatch) {
        return {
          value: strId,
          label: rawMatch.raw?.license_plate ?? rawMatch.original?.license_plate ?? rawMatch.label ?? strId,
          companyName: rawMatch.companyName,
          raw: rawMatch.raw
        };
      }
      return { value: strId, label: strId };
    });
    setSelectedVehicles(newSelected);
  };

  useEffect(() => {
    if (fromDate && toDate) {
      const start = dayjs(fromDate);
      const end = dayjs(toDate);
      const diffDays = end.diff(start, 'day');

      if (diffDays > 31) {
        setDateError(t('dailyFuel.maxTime31Days', 'Maximum time span of 31 days'));
      } else if (end.isBefore(start)) {
        setDateError(t('dailyFuel.endTimeCant', 'End date cannot be before start date'));
      } else {
        setDateError('');
      }
    } else {
      setDateError('');
    }
  }, [fromDate, t, toDate]);

  const fetchData = useCallback(async () => {
    if (selectedCompanyIds.length === 0 || !fromDate || !toDate || selectedVehicleIds.length === 0 || dateError) {
      if (rows.length > 0 && (selectedVehicleIds.length === 0 || !fromDate || !toDate)) {
        setRows([]);
        setSummaryData({
          fuelConsumption: '0',
          fuelConsumptionCanbus: '0',
          fillVolume: '0',
          residualVolume: '0',
          totalDistance: '0',
          durationOn: '00:00:00',
          durationOff: '00:00:00'
        });
      }
      return;
    }
    if (selectedVehicleIds.length === 0) {
      notify({ message: t('dailyFuel.selectOneVehicle', 'Select at least one vehicle'), severity: 'warning' });
      return;
    }
    if (!fromDate || !toDate) {
      notify({ message: t('dailyFuel.selectPeriode', 'Please select a date period'), severity: 'warning' });
      return;
    }
    if (dayjs(fromDate).isAfter(dayjs(toDate))) {
      notify({ message: t('dailyFuel.fromDateToDate', 'The From date cannot be greater than the To date.'), severity: 'error' });
      return;
    }
    setLoading(true);
    try {
      let apiChunks = [];
      const localStart = dayjs(fromDate).second(0);
      const localEnd = dayjs(toDate).second(59);
      const pType = periodType;
      const isFullDayPeriod = ['week', 'last_week', 'month', 'last_month'].includes(pType);

      if (isFullDayPeriod) {
        let curr = localStart.startOf('day');
        const end = localEnd.startOf('day');
        while (curr.isBefore(end) || curr.isSame(end, 'day')) {
          apiChunks.push({
            startLocal: curr.startOf('day'),
            endLocal: curr.endOf('day'),
            dateObj: curr,
            dateStr: curr.format('YYYY-MM-DD')
          });
          curr = curr.add(1, 'day');
        }
      } else {
        let curr = localStart.startOf('day');
        const end = localEnd.startOf('day');

        while (curr.isBefore(end) || curr.isSame(end, 'day')) {
          let chunkStart = curr.startOf('day');
          let chunkEnd = curr.endOf('day');

          if (curr.isSame(localStart, 'day')) chunkStart = localStart;
          if (curr.isSame(localEnd, 'day')) chunkEnd = localEnd;

          apiChunks.push({
            startLocal: chunkStart,
            endLocal: chunkEnd,
            dateObj: curr,
            dateStr: curr.format('YYYY-MM-DD')
          });

          curr = curr.add(1, 'day');
        }
      }
      let targetIds = [];
      const isSelectAll = selectedVehicleIds.length === vehicleOptionsNormalized.length;

      if (isSelectAll) {
        targetIds = vehicleOptionsNormalized.map((v) => String(v.value));
      } else {
        targetIds = selectedVehicleIds;
      }

      if (targetIds.length === 0) {
        setRows([]);
        setLoading(false);
        return;
      }
      setLoadingProgress(0);
      setLoadingText('Fetching data...');
      const totalVehicles = targetIds.length;
      let completedVehicles = 0;
      const chunkSize = 5;
      const responses = [];

      for (let i = 0; i < totalVehicles; i += chunkSize) {
        const chunk = targetIds.slice(i, i + chunkSize);
        const chunkPromises = chunk.map(async (vehicleId) => {
          const vInfo =
            selectedVehicles.find((v) => String(v.value) === String(vehicleId)) ||
            vehicleOptionsNormalized.find((v) => String(v.value) === String(vehicleId));
          const vCompanyId = vInfo?.raw?.company || vInfo?.raw?.join?.company_code || 0;
          const licensePlate = vInfo?.label || String(vehicleId);

          const tz = typeof timezone === 'function' ? timezone() : Number(timezone) || 7;
          const datePromises = apiChunks.map((chunk) => {
            const apiBeginTime = chunk.startLocal.subtract(tz, 'hour').format('YYYY-MM-DDTHH:mm:ss[Z]');
            const apiEndTime = chunk.endLocal.subtract(tz, 'hour').format('YYYY-MM-DDTHH:mm:ss[Z]');
            const dateObj = chunk.dateObj;

            const payload = {
              company_id: Number(vCompanyId),
              start_date: apiBeginTime,
              end_date: apiEndTime,
              vehicle_id: Number(vehicleId),
              tz
            };

            return filterReportFuelSummary(payload, tz)
              .then((response) => ({
                apiData: response,
                requestDate: dateObj
              }))
              .catch((err) => ({
                apiData: null,
                requestDate: dateObj,
                error: err
              }));
          });

          try {
            const vehicleResponses = await Promise.all(datePromises);
            return vehicleResponses;
          } finally {
            completedVehicles++;
            setLoadingProgress(Math.round((completedVehicles / totalVehicles) * 100));
            setLoadingText(`Processing unit: ${licensePlate} (${completedVehicles}/${totalVehicles})`);
          }
        });

        const chunkResponses = await Promise.all(chunkPromises);
        responses.push(...chunkResponses.flat());
      }
      const accumulatedData = responses.map((item) => {
        // Ambil data asli dan tanggal yang kita sisipkan tadi
        const { apiData, requestDate } = item;
        const rowData = apiData?.data || {};
        return {
          ...rowData,
          // Ngebuattanggal dari loop
          date: requestDate ? requestDate.format('DD/MM/YYYY') : '-',
          id: `${rowData.vehicle_id || 'unknown'}_${requestDate.valueOf()}`
        };
      });
      const validData = accumulatedData.filter((item) => item.vehicle_id);
      setRows(validData);
      setSummaryData({
        fuelConsumption: fuelConsumptionCount(validData),
        fuelConsumptionCanbus: fuelConsumptionCanbusCount(validData),
        fillVolume: fillVolumeCount(validData),
        residualVolume: residualVolumeCount(validData),
        totalDistance: totalDistanceCount(validData),
        durationOn: totalDurationOnCount(validData),
        durationOff: totalDurationOffCount(validData)
      });
    } catch (error) {
      console.error('Gagal mengambil data report:', error);
      setRows([]);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fromDate, toDate, selectedVehicleIds, dateError, rows.length, notify, vehicleOptionsNormalized]);
  const filteredRows = useMemo(() => {
    let result = rows;
    if (tableSearchQuery) {
      const lowerQ = tableSearchQuery.toLowerCase();
      result = result.filter(
        (row) =>
          (row.plate || row.license_plate || '').toLowerCase().includes(lowerQ) ||
          (row.type || row.vehicle_type_name || '').toLowerCase().includes(lowerQ)
      );
    }
    return result;
  }, [rows, tableSearchQuery]);

  const residualVolumeData = useMemo(() => {
    const grouped = {};
    filteredRows.forEach((row) => {
      const vid = row.vehicle_id;
      if (!grouped[vid]) {
        grouped[vid] = [];
      }
      grouped[vid].push(row);
    });

    const list = [];
    let grandTotal = 0;

    Object.keys(grouped).forEach((vid) => {
      const vehicleRows = grouped[vid];
      vehicleRows.sort((a, b) => {
        const parseDate = (d) => {
          if (!d || d === '-') return 0;
          const parts = d.split('/');
          if (parts.length === 3) {
            return dayjs(`${parts[2]}-${parts[1]}-${parts[0]}`).valueOf();
          }
          return 0;
        };
        return parseDate(b.date) - parseDate(a.date);
      });

      const lastRow = vehicleRows[0];
      let residual = 0;
      if (fuelSource === 'canbus') {
        residual = Number(lastRow.end_fuel_consumed_canbus) || 0;
      } else {
        const arr = Array.isArray(lastRow.calculate_fuel) ? lastRow.calculate_fuel : [];
        if (arr.length > 0) {
          residual = Number(arr[arr.length - 1].end_fuel_level) || 0;
        }
      }

      grandTotal += residual;
      list.push({
        vehicle_id: vid,
        license_plate: lastRow.license_plate || '-',
        residual: residual
      });
    });

    return { list, grandTotal };
  }, [filteredRows, fuelSource]);

  const exportExcel = async () => {
    if (!filteredRows || filteredRows.length === 0) {
      notify({ message: t('dailyFuel.noDataExport', 'No data to export'), severity: 'warning' });
      return;
    }
    const workbook = new ExcelJS.Workbook();
    const sheet = workbook.addWorksheet('Daily Fuel Summary');

    // Headers Row 1
    sheet.mergeCells('A1:A3');
    sheet.getCell('A1').value = t('time.date', 'Tanggal');
    sheet.mergeCells('B1:B3');
    sheet.getCell('B1').value = t('masterVehicle.licensePlate', 'License Plate');
    sheet.mergeCells('C1:C3');
    sheet.getCell('C1').value = t('menu.vehicle-type', 'Vehicle Type');

    let currentColumn = 4; // D

    if (fuelSource === 'sensor' || fuelSource === 'all') {
      const getColLetter = (index) => String.fromCharCode(64 + index); // Assumes index <= 26
      const startCol = getColLetter(currentColumn);
      const endCol = getColLetter(currentColumn + 5);

      sheet.mergeCells(`${startCol}1:${endCol}1`);
      sheet.getCell(`${startCol}1`).value = 'Fuel By Calculate Sensor';

      sheet.mergeCells(`${startCol}2:${getColLetter(currentColumn + 1)}2`);
      sheet.getCell(`${startCol}2`).value = t('dailyFuel.startVol', 'Start Volume');
      sheet.getCell(`${startCol}3`).value = '%';
      sheet.getCell(`${getColLetter(currentColumn + 1)}3`).value = 'Vol (L)';

      sheet.mergeCells(`${getColLetter(currentColumn + 2)}2:${getColLetter(currentColumn + 3)}2`);
      sheet.getCell(`${getColLetter(currentColumn + 2)}2`).value = t('dailyFuel.endVol', 'End Volume');
      sheet.getCell(`${getColLetter(currentColumn + 2)}3`).value = '%';
      sheet.getCell(`${getColLetter(currentColumn + 3)}3`).value = 'Vol (L)';

      sheet.mergeCells(`${getColLetter(currentColumn + 4)}2:${getColLetter(currentColumn + 5)}2`);
      sheet.getCell(`${getColLetter(currentColumn + 4)}2`).value = 'Est ' + t('dailyFuel.refueling', 'Refueling') + ' (L)';
      sheet.getCell(`${getColLetter(currentColumn + 4)}3`).value = '%';
      sheet.getCell(`${getColLetter(currentColumn + 5)}3`).value = 'Vol (L)';

      currentColumn += 6;
    }

    if (fuelSource === 'canbus' || fuelSource === 'all') {
      const getColLetter = (index) => String.fromCharCode(64 + index); // P is 16
      const startCol = getColLetter(currentColumn);
      const endCol = getColLetter(currentColumn + 2);

      sheet.mergeCells(`${startCol}1:${endCol}1`);
      sheet.getCell(`${startCol}1`).value = 'Fuel Consumption By Canbus';

      sheet.mergeCells(`${startCol}2:${startCol}3`);
      sheet.getCell(`${startCol}2`).value = t('dailyFuel.startVol', 'Start Volume') + ' (L)';

      sheet.mergeCells(`${getColLetter(currentColumn + 1)}2:${getColLetter(currentColumn + 1)}3`);
      sheet.getCell(`${getColLetter(currentColumn + 1)}2`).value = t('dailyFuel.endVol', 'End Volume') + ' (L)';

      sheet.mergeCells(`${getColLetter(currentColumn + 2)}2:${getColLetter(currentColumn + 2)}3`);
      sheet.getCell(`${getColLetter(currentColumn + 2)}2`).value = 'Total Consump (L)';

      currentColumn += 3;
    }

    const getColLetterFin = (index) => String.fromCharCode(64 + index);
    const milCol = getColLetterFin(currentColumn);
    const travCol = getColLetterFin(currentColumn + 1);
    const idleCol = getColLetterFin(currentColumn + 2);
    const actCol = getColLetterFin(currentColumn + 3);

    sheet.mergeCells(`${milCol}1:${milCol}3`);
    sheet.getCell(`${milCol}1`).value = t('dailyFuel.totalMileage', 'Total Mileage') + ' (KM)';

    sheet.mergeCells(`${travCol}1:${travCol}3`);
    sheet.getCell(`${travCol}1`).value = t('dailyFuel.travelingTime', 'Traveling Time');

    sheet.mergeCells(`${idleCol}1:${idleCol}3`);
    sheet.getCell(`${idleCol}1`).value = t('dailyFuel.idleTime', 'Idle Time');

    sheet.mergeCells(`${actCol}1:${actCol}3`);
    sheet.getCell(`${actCol}1`).value = t('dailyFuel.actual', 'Actual') + ' (KM/L)';

    // Styling headers
    const lastColIndex = currentColumn + 3;
    for (let c = 1; c <= lastColIndex; c++) {
      for (let r = 1; r <= 3; r++) {
        const cell = sheet.getCell(r, c);
        if (cell.value) {
          cell.font = { bold: true, color: { argb: 'FFFFFFFF' } };
          cell.alignment = { vertical: 'middle', horizontal: 'center' };
          cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF485D9A' } };
        }
      }
    }

    filteredRows.forEach((row) => {
      const tank = Number(row.fuel_tank) || 0;
      const arr = Array.isArray(row.calculate_fuel) ? row.calculate_fuel : [];
      let volAwalPct = '0.0%',
        volAwal = 0;
      let volAkhirPct = '0.0%',
        volAkhir = 0;
      let refuelPct = '0.0%',
        refuelVol = 0;

      if (arr.length > 0) {
        const sVol = Number(arr[0].start_fuel_level) || 0;
        volAwal = sVol;
        volAwalPct = tank > 0 ? ((sVol / tank) * 100).toFixed(1) + '%' : '0.0%';

        const eVol = Number(arr[arr.length - 1].end_fuel_level) || 0;
        volAkhir = eVol;
        volAkhirPct = tank > 0 ? ((eVol / tank) * 100).toFixed(1) + '%' : '0.0%';

        const tRest = arr.reduce((sum, item) => sum + (Number(item.rest_fuel_level) || 0), 0);
        refuelVol = tRest;
        refuelPct = tank > 0 ? ((tRest / tank) * 100).toFixed(1) + '%' : '0.0%';
      }

      // Canbus
      const canStart = Number(row.start_fuel_consumed_canbus) || 0;
      const canEnd = Number(row.end_fuel_consumed_canbus) || 0;
      const canTotal = Number(row.total_fuel_consumed_canbus) || 0;

      const eff = Number(row.km_to_litre).toFixed(2) || 0;
      const effCanbus = Number(row.km_to_litre_canbus).toFixed(2) || 0;
      let act = fuelSource === 'canbus' ? effCanbus : eff;

      const rowData = [row.date || '-', row.license_plate || '-', row.type || row.vehicle_type_name || '-'];

      if (fuelSource === 'sensor' || fuelSource === 'all') {
        rowData.push(volAwalPct, volAwal, volAkhirPct, volAkhir, refuelPct, refuelVol);
      }

      if (fuelSource === 'canbus' || fuelSource === 'all') {
        rowData.push(canStart, canEnd, canTotal);
      }

      rowData.push(row.distance || 0, row.total_engine_on || '00:00:00', row.idle_time || '00:00:00', act);

      sheet.addRow(rowData);
    });

    sheet.columns.forEach((column) => {
      let maxLen = 10;
      column.eachCell({ includeEmpty: true }, (cell) => {
        if (cell.value) {
          const valLen = cell.value.toString().length;
          if (valLen > maxLen) maxLen = valLen;
        }
      });
      column.width = maxLen + 2;
    });

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `Daily_Fuel_Summary_${dayjs().format('YYYYMMDD_HHmmss')}.xlsx`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Paper
        elevation={0}
        sx={{
          p: 1.5,
          mb: 0.5,
          borderRadius: 3,
          border: '1px solid',
          borderColor: 'divider',
          background: 'linear-gradient(to right bottom, #ffffff, #fcfcfc)'
        }}
      >
        <Box
          sx={{
            p: 1,
            px: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            mb: 2,
            bgcolor: 'rgba(25, 118, 210, 0.04)',
            borderRadius: 2
          }}
        >
          <Typography variant="body2" color="#1976d2" fontWeight="500" sx={{ lineHeight: 1.5 }}>
            {t(
              'reports.dailySummaryDesc',
              'Daily accumulation of fuel: estimated refueling, reduction, usage, efficiency, and total cost.'
            )}
          </Typography>
          {(localStorage.getItem('rolename') === 'Administrator' ||
            (() => {
              try {
                return JSON.stringify(JSON.parse(localStorage.getItem('master_menu') || '[]')).includes('"modules_path":"report-catalog"');
              } catch {
                return false;
              }
            })()) && (
            <Box>
              <Button
                startIcon={<ArrowBackIcon fontSize="small" />}
                onClick={() => navigate('/report-catalog?name=report-catalog')}
                size="small"
                variant="contained"
                sx={{
                  bgcolor: 'primary',
                  color: '#ffffff',
                  textTransform: 'none',
                  px: 0.5,
                  py: 0,
                  minWidth: 0,
                  '&:hover': { bgcolor: '#36477c', color: '#ffffff' }
                }}
              >
                Report Catalog
              </Button>
            </Box>
          )}
        </Box>
        <Box display="flex" flexWrap="wrap" gap={2.5} alignItems="center">
          <Box sx={{ flex: '1 1 200px' }}>
            <PickerField
              label={t('general.company', 'Company')}
              value={selectedCompanyIds}
              {...companyPickerParams}
              options={companyOptions}
              onChange={(ids) => {
                setSelectedCompanyIds(ids || []);
                setSelectedVehicles([]);
              }}
              dialogTitle={t('general.selectCompany', 'Select Company')}
              multiSelect={true}
            />
          </Box>
          <Box sx={{ flex: '1 1 200px' }}>
            <PickerField
              label={t('general.selectVehicle', 'Select Vehicle')}
              value={selectedVehicleIds}
              selectedItems={selectedVehicles}
              {...vehiclePickerParams}
              options={vehicleOptionsNormalized}
              onChange={handleVehicleChange}
              dialogTitle={t('general.selectVehicle', 'Select Vehicle')}
              multiSelect={true}
            />
          </Box>
          <Box sx={{ flex: '0 1 150px' }}>
            <FormControl fullWidth size="medium">
              <InputLabel id="period-label">{t('general.selectPeriod', 'Select Period')}</InputLabel>
              <MuiSelect
                labelId="period-label"
                value={periodType}
                onChange={(e) => setPeriodType(e.target.value)}
                disabled={loading}
                input={<OutlinedInput label={t('general.selectPeriod', 'Select Period')} />}
                sx={{ bgcolor: 'background.paper' }}
              >
                <MenuItem value="today">Today</MenuItem>
                <MenuItem value="week">{t('time.lastWeek', 'Last Week')}</MenuItem>
                <MenuItem value="month">Last Month</MenuItem>
                <MenuItem value="custom">Custom</MenuItem>
              </MuiSelect>
            </FormControl>
          </Box>
          <Box sx={{ flex: '0 1 150px' }}>
            <FormControl fullWidth size="medium">
              <InputLabel id="fuel-source-label">{t('general.fuelSource', 'Fuel Source')}</InputLabel>
              <MuiSelect
                labelId="fuel-source-label"
                value={fuelSource}
                onChange={(e) => setFuelSource(e.target.value)}
                disabled={loading}
                input={<OutlinedInput label={t('general.fuelSource', 'Fuel Source')} />}
                sx={{ bgcolor: 'background.paper' }}
              >
                <MenuItem value="all">{t('general.all', 'All')}</MenuItem>
                <MenuItem value="sensor">{t('general.bySensor', 'By Sensor')}</MenuItem>
                <MenuItem value="canbus">{t('general.byCanbus', 'By Canbus')}</MenuItem>
              </MuiSelect>
            </FormControl>
          </Box>

          {periodType === 'custom' && (
            <Box sx={{ flex: '1 1 350px' }}>
              <Stack direction="row" spacing={2}>
                <DateTimePicker
                  ampm={false}
                  label={t('general.from', 'From')}
                  timeSteps={{ minutes: 1 }}
                  format="DD/MM/YYYY HH:mm"
                  value={fromDate ? dayjs(fromDate) : null}
                  onChange={(v) => {
                    const newV = v ? v.second(0).format('YYYY-MM-DD HH:mm:ss') : '';
                    setFromDate(newV);
                    validateDateRange(dayjs(newV), dayjs(toDate));
                  }}
                  maxDate={toDate ? dayjs(toDate) : undefined}
                  disabled={loading}
                  slotProps={{ textField: { size: 'small', fullWidth: true, error: !!dateError, sx: { bgcolor: 'background.paper' } } }}
                />
                <DateTimePicker
                  ampm={false}
                  label={t('general.to', 'To')}
                  timeSteps={{ minutes: 1 }}
                  format="DD/MM/YYYY HH:mm"
                  value={toDate ? dayjs(toDate) : null}
                  onChange={(v) => {
                    const newV = v ? v.second(59).format('YYYY-MM-DD HH:mm:ss') : '';
                    setToDate(newV);
                    validateDateRange(dayjs(fromDate), dayjs(newV));
                  }}
                  minDate={fromDate ? dayjs(fromDate) : undefined}
                  disabled={loading}
                  slotProps={{ textField: { size: 'small', fullWidth: true, error: !!dateError, sx: { bgcolor: 'background.paper' } } }}
                />
              </Stack>
            </Box>
          )}

          <Box sx={{ display: 'flex', gap: 1.5, ml: 'auto', mt: { xs: 2, md: 0 } }}>
            <Button
              variant="outlined"
              color="primary"
              startIcon={<DownloadIcon />}
              onClick={exportExcel}
              disabled={loading || filteredRows.length === 0}
              sx={{ px: 3, fontWeight: 'bold', boxShadow: 2, bgcolor: '#ffffff' }}
            >
              {t('general.export', 'Export')}
            </Button>
            <Button
              variant="outlined"
              color="inherit"
              disabled={loading}
              onClick={() => {
                setSelectedCompanyIds(() => {
                  const defaultId = getCompanyIdFromStorage();
                  return defaultId ? [defaultId] : [];
                });
                setSelectedVehicles([]);
                setTableSearchQuery('');
                setRows([]);
                setPeriodType('today');
                setFuelSource('all');
                setSummaryData({
                  fuelConsumption: '0',
                  fillVolume: '0',
                  residualVolume: '0',
                  totalDistance: '0',
                  durationOn: '00:00:00',
                  durationOff: '00:00:00'
                });
                setDateError('');
              }}
              sx={{ px: 2, minWidth: 0, bgcolor: '#ffffff' }}
            >
              <RestartAltIcon />
            </Button>
            <Button
              variant="contained"
              color="primary"
              startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <SearchIcon />}
              onClick={fetchData}
              disabled={loading || !!dateError || selectedVehicleIds.length === 0}
              sx={{ px: 3, fontWeight: 'bold', boxShadow: 2 }}
            >
              {loading ? t('general.searching', 'Searching') : t('general.search', 'Search')}
            </Button>
          </Box>
        </Box>
        {dateError && (
          <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
            {dateError}
          </Typography>
        )}
      </Paper>
      {loading && loadingProgress > 0 && (
        <Box
          sx={{ p: 4, textAlign: 'center', bgcolor: '#fff', borderRadius: 2, mb: 2, mt: 2, border: '1px dashed rgba(72, 93, 154, 0.3)' }}
        >
          <Box sx={{ width: '60%', mx: 'auto', mt: 2, mb: 2, textAlign: 'center' }}>
            <LinearProgress
              variant="determinate"
              value={loadingProgress}
              sx={{
                height: 10,
                borderRadius: 5,
                mb: 1.5,
                bgcolor: 'rgba(72, 93, 154, 0.2)',
                '& .MuiLinearProgress-bar': { bgcolor: '#485D9A' }
              }}
            />
            <Typography variant="body2" color="text.secondary" fontWeight="bold">
              {loadingProgress}% - {loadingText}
            </Typography>
          </Box>
        </Box>
      )}

      {rows.length > 0 && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            mb: 0.5,
            px: 2,
            py: 1.5,
            borderRadius: 2,
            bgcolor: 'rgba(72, 93, 154, 0.05)',
            border: '1px solid rgba(72, 93, 154, 0.2)',
            boxShadow: '0 2px 8px rgba(72, 93, 154, 0.05)'
          }}
        >
          <InfoOutlinedIcon sx={{ color: '#485D9A' }} />
          <Typography variant="body2" sx={{ color: '#333', fontWeight: 500 }}>
            {t('revenue.showingDataFrom', 'Showing data from')}{' '}
            <Box component="span" sx={{ color: '#485D9A', fontWeight: 'bold' }}>
              {dayjs(fromDate).format('DD MMMM YYYY HH:mm:ss')}
            </Box>
            {t('revenue.to', 'to')}
            <Box component="span" sx={{ color: '#485D9A', fontWeight: 'bold' }}>
              {dayjs(toDate).format('DD MMMM YYYY HH:mm:ss')}
            </Box>
          </Typography>
        </Box>
      )}
      <Paper sx={{ p: { xs: 2, md: 3 }, mb: 2, borderRadius: 1, boxShadow: 2 }}>
        <Typography variant="h5" fontWeight="700" color="text.primary" gutterBottom sx={{ mb: 3 }}>
          {t('general.dataSummary', 'Data Summary')}
        </Typography>
        <Grid sx={{ display: 'flex', justifyContent: 'center' }}>
          <Grid container rowSpacing={1} columnSpacing={{ xs: 1, sm: 2, md: 3 }}>
            {/* Item 1: Fuel Consumption */}
            <Grid size={6}>
              <Box
                sx={{
                  bgcolor: 'rgba(72, 93, 154, 0.04)',
                  border: '1px solid',
                  borderColor: 'rgba(72, 93, 154, 0.15)',
                  // minWidth:360,
                  borderRadius: 3,
                  p: 2,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2, // Jarak antara ikon dan teks
                  transition: 'all 0.2s',
                  '&:hover': {
                    boxShadow: '0 4px 12px rgba(72, 93, 154, 0.1)',
                    borderColor: '#485D9A'
                  }
                }}
              >
                <Box sx={{ p: 1.5, bgcolor: '#ffffff', borderRadius: 2, display: 'flex', boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}>
                  <LocalGasStationIcon sx={{ color: '#485D9A', fontSize: 28 }} />
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', alignItems: 'center' }}>
                  {(fuelSource === 'canbus' || fuelSource === 'all') && (
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="body2" color="text.secondary" fontWeight="500">
                        {t('dailyFuel.fuelConsumption', 'Fuel Consumption')} (Canbus)
                      </Typography>
                      <Typography variant="h6" fontWeight="700" color="text.primary" sx={{ mt: 0.5 }}>
                        {summaryData.fuelConsumptionCanbus || '0'}
                        <Typography component="span" variant="body2" color="text.secondary" fontWeight="600">
                          Liter
                        </Typography>
                      </Typography>
                    </Box>
                  )}

                  {(fuelSource === 'sensor' || fuelSource === 'all') && (
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="body2" color="text.secondary" fontWeight="500">
                        {t('fuelConsumption', 'Fuel Consumption')} (Sensor)
                      </Typography>
                      <Typography variant="h6" fontWeight="700" color="text.primary" sx={{ mt: 0.5 }}>
                        {summaryData.fuelConsumption || '0'}{' '}
                        <Typography component="span" variant="body2" color="text.secondary" fontWeight="600">
                          Liter
                        </Typography>
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Box>
            </Grid>

            {/* Item 2: Volume Pengisian */}
            <Grid size={6}>
              <Box
                sx={{
                  bgcolor: 'rgba(72, 93, 154, 0.04)',
                  border: '1px solid',
                  borderColor: 'rgba(72, 93, 154, 0.15)',
                  borderRadius: 3,
                  // minWidth:360,
                  p: 2,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                  transition: 'all 0.2s',
                  '&:hover': {
                    boxShadow: '0 4px 12px rgba(72, 93, 154, 0.1)',
                    borderColor: '#485D9A'
                  }
                }}
              >
                <Box sx={{ p: 1.5, bgcolor: '#ffffff', borderRadius: 2, display: 'flex', boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}>
                  <PropaneIcon sx={{ color: '#FCB859', fontSize: 28 }} />
                </Box>
                <Box>
                  <Typography variant="body2" color="text.secondary" fontWeight="500">
                    Est {t('dailyFuel.refuelingVolume', 'Refueling Volume')}
                  </Typography>
                  <Typography variant="h5" fontWeight="700" color="text.primary" sx={{ mt: 0.5 }}>
                    {summaryData.fillVolume || '0'}{' '}
                    <Typography component="span" variant="body2" color="text.secondary" fontWeight="600">
                      Liter
                    </Typography>
                  </Typography>
                </Box>
              </Box>
            </Grid>

            {/* Item 3: Volume Sisa */}
            <Grid size={6}>
              <ResidualVolumeCard residualData={residualVolumeData} t={t} />
            </Grid>

            {/* Item 4: Total Jarak Tempuh */}
            <Grid size={6}>
              <Box
                sx={{
                  bgcolor: 'rgba(72, 93, 154, 0.04)',
                  border: '1px solid',
                  borderColor: 'rgba(72, 93, 154, 0.15)',
                  borderRadius: 3,
                  p: 2,
                  // minWidth:360,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                  transition: 'all 0.2s',
                  '&:hover': {
                    boxShadow: '0 4px 12px rgba(72, 93, 154, 0.1)',
                    borderColor: '#485D9A'
                  }
                }}
              >
                <Box sx={{ p: 1.5, bgcolor: '#ffffff', borderRadius: 2, display: 'flex', boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}>
                  <RouteIcon sx={{ color: '#485D9A', fontSize: 28 }} />
                </Box>
                <Box>
                  <Typography variant="body2" color="text.secondary" fontWeight="500">
                    {t('dailyFuel.totalMileage', 'Total Mileage')}
                  </Typography>
                  <Typography variant="h5" fontWeight="700" color="text.primary" sx={{ mt: 0.5 }}>
                    {summaryData.totalDistance || '0'}{' '}
                    <Typography component="span" variant="body2" color="text.secondary" fontWeight="600">
                      Km
                    </Typography>
                  </Typography>
                </Box>
              </Box>
            </Grid>

            {/* Item 5: Total Durasi Berjalan */}
            <Grid size={6}>
              <Box
                sx={{
                  bgcolor: 'rgba(72, 93, 154, 0.04)',
                  border: '1px solid',
                  borderColor: 'rgba(72, 93, 154, 0.15)',
                  borderRadius: 3,
                  // minWidth:360,
                  p: 2,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                  transition: 'all 0.2s',
                  '&:hover': {
                    boxShadow: '0 4px 12px rgba(72, 93, 154, 0.1)',
                    borderColor: '#485D9A'
                  }
                }}
              >
                <Box sx={{ p: 1.5, bgcolor: '#ffffff', borderRadius: 2, display: 'flex', boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}>
                  <PlayCircleOutlineIcon sx={{ color: '#00C93E', fontSize: 28 }} />
                </Box>
                <Box>
                  <Typography variant="body2" color="text.secondary" fontWeight="500">
                    {t('dailyFuel.totalDuration', 'Total Duration')}
                  </Typography>
                  <Typography variant="h5" fontWeight="700" color="text.primary" sx={{ mt: 0.5 }}>
                    {summaryData.durationOn || '00:00:00'}
                  </Typography>
                </Box>
              </Box>
            </Grid>

            {/* Item 6: Total Durasi Diam */}
            <Grid size={6}>
              <Box
                sx={{
                  bgcolor: 'rgba(72, 93, 154, 0.04)',
                  border: '1px solid',
                  borderColor: 'rgba(72, 93, 154, 0.15)',
                  borderRadius: 3,
                  // minWidth:360,
                  p: 2,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                  transition: 'all 0.2s',
                  '&:hover': {
                    boxShadow: '0 4px 12px rgba(72, 93, 154, 0.1)',
                    borderColor: '#485D9A'
                  }
                }}
              >
                <Box sx={{ p: 1.5, bgcolor: '#ffffff', borderRadius: 2, display: 'flex', boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}>
                  <PauseCircleOutlineIcon sx={{ color: '#E60000', fontSize: 28 }} />
                </Box>
                <Box>
                  <Typography variant="body2" color="text.secondary" fontWeight="500">
                    Total {t('dailyFuel.idleTime', 'Idle Time')}
                  </Typography>
                  <Typography variant="h5" fontWeight="700" color="text.primary" sx={{ mt: 0.5 }}>
                    {summaryData.durationOff || '00:00:00'}
                  </Typography>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Grid>
      </Paper>
      {/* Tampilkan Component Native MUI Table Khusus */}
      <Box mt={2}>
        <DataTableDailyFuelSummary rows={filteredRows} loading={loading} fuelSource={fuelSource} />
      </Box>
    </LocalizationProvider>
  );
}
export default function DailyFuelSummaryPage() {
  return (
    <>
      <DailyFuelSummaryPageContent />
    </>
  );
}
